import { FRAAutocomplete } from "Components/fra-e-autocomplete/fra-e-autocomplete";
import { onEvent, removeClass } from "@kluntje/js-utils/lib/dom-helpers";
import { ACTIVE } from "Helper/cssClasses";

export class FRAWaitingTimesTeaserInput extends FRAAutocomplete {
  /**
   * returns the value of the inner input element
   * @returns {string}
   */
  public get value(): string {
    return this.ui.input.value;
  }

  /**
   * sets the value of the inner input element
   * @param {string} value
   */
  public set value(value: string) {
    this.ui.input.value = value;
  }

  /**
   * returns a list of rendered anchors for suggestions
   */
  private get suggestions(): HTMLElement[] {
    return Array.from(this.querySelectorAll<HTMLElement>(".fra-js-waiting-times-teaser__suggestion-link"));
  }

  /**
   * returns the anchor for the suggestions which is highlighted,
   * the first suggestion after render, or the one user has currenlty highlighted via key up and down
   */
  public get activeSuggestion() {
    return this.suggestions.find(el => el.classList.contains(ACTIVE));
  }

  constructor() {
    super({
      ui: {
        searchBtn: ".fra-js-waiting-times-teaser__submit",
      },
      events: [
        {
          event: "keydown",
          target: "input",
          handler: "handleSpecialKeys",
        },
        {
          event: "click",
          target: "searchBtn",
          handler: "handleSubmit",
        },
      ],
    });
  }

  /**
   * bind click events to suggestion links
   */
  bindEvents(): void {
    const suggestions = this.suggestions;

    suggestions.forEach(item => {
      onEvent(
        item,
        "click",
        e => {
          e.preventDefault();
          const key = (e.currentTarget as HTMLElement).getAttribute("data-key")!;
          const q = (e.currentTarget as HTMLElement).getAttribute("data-q")!;
          this.value = q;
          this.dispatchEvent(new CustomEvent("fra-set-waiting-times-teaser-to-suggestion", { detail: { key, q } }));
          removeClass(this, ACTIVE);
        },
        this,
      );
    });
  }

  /**
   * @override
   * Render suggestions in list
   * @param {Array<Suggestion>} suggestions
   */
  renderSuggestions(suggestions: Array<any>): void {
    const autocompleteItems = suggestions.reduce((result, suggestion: { display: string; keyword: string }, ix) => {
      result += `<li class="fra-e-autocomplete__list-item">
          <a class="fra-e-link fra-e-link--with-icon fra-e-link--animated-right fra-m-waiting-times-teaser__suggestion-link fra-js-waiting-times-teaser__suggestion-link ${
            ix === 0 ? ACTIVE : ""
          }"
            data-key="${suggestion.keyword}"
            data-q="${suggestion.display}"
            data-gaTracking="{&quot;event&quot;:&quot;gaEvent&quot;,&quot;eventCategory&quot;:&quot;WaitingTimeTeaser&quot;,&quot;eventAction&quot;:&quot;${
              suggestion.display
            }&quot;,&quot;eventLabel&quot;:&quot;${suggestion.keyword
        .replace(/[^A-Za-z 0-9]+|\s+/g, "")
        .toLowerCase()}&quot;}"
            href="#${suggestion.keyword.replace(/[^A-Za-z 0-9]+|\s+/g, "").toLowerCase()}"
          >
            <span class="fra-e-link__label">${suggestion.display}</span>
            <fra-e-icon class="fra-e-icon" icon-id="fra-icon-arrow-right"></fra-e-icon>
          </a>
        </li>`;

      return result;
    }, "");

    if (this.state.loaded) {
      this.ui.autocompleteList.innerHTML =
        suggestions.length === 0
          ? `<li class="fra-e-autocomplete__list-item"><span class="fra-m-invalid-flightnumber">${this.ui.input.getAttribute(
              "data-invalid-text",
            )}</span></li>`
          : autocompleteItems;
      this.bindEvents();
      this.showAutocompleteList();
    }
  }

  /**
   * @override
   * reset input to default state and caise an new search with default values
   */
  resetInput(): void {
    super.resetInput();
    this.dispatchEvent(new CustomEvent("fra-set-waiting-times-teaser-to-suggestion", { detail: { key: "", q: "" } }));
    removeClass(this, ACTIVE);
  }

  handleSpecialKeys(e: KeyboardEvent) {
    const suggestions = this.suggestions;

    switch (e.key) {
      case "Escape":
        this.value = "";
        break;

      case "ArrowUp":
        // there is always one suggestion highlighted,
        // unless there isn't any suggestions at all
        if (this.activeSuggestion) {
          // prevent cursor from going back/forward on the input letters
          e.preventDefault();
          const activeIndex = suggestions.indexOf(this.activeSuggestion);
          this.activeSuggestion.classList.remove(ACTIVE);
          suggestions[(activeIndex - 1 + suggestions.length) % suggestions.length].classList.add(ACTIVE);
        }
        break;

      case "ArrowDown":
        if (this.activeSuggestion) {
          // prevent cursor from going back/forward on the input letters
          e.preventDefault();
          const activeIndex = suggestions.indexOf(this.activeSuggestion);
          this.activeSuggestion.classList.remove(ACTIVE);
          suggestions[(activeIndex + 1) % suggestions.length].classList.add(ACTIVE);
        }
        break;

      // Enter is handled by the submit handler automatically

      default:
        break;
    }
  }

  handleSubmit(e: UIEvent) {
    // it is a submit button in a form.
    // don't submit the form.
    // only select the highlighted suggestion
    e.preventDefault();

    // make sure there are any suggestions,
    // and those aren't the ones fetched when the value was longer than the threshold
    if (this.value.length >= FRAAutocomplete.INPUT_MIN_LENGTH && this.activeSuggestion) {
      this.activeSuggestion.click();
      this.ui.input.blur(); // same behavior as when user clicks on a suggestion and the results is shown
    }
  }
}

customElements.define("fra-m-waiting-times-teaser-input", FRAWaitingTimesTeaserInput);

export default FRAWaitingTimesTeaserInput;
